// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { z } from 'zod';
import { MAX_LIST_PAGE_SIZE, DEFAULT_LIST_PAGE_SIZE } from '../constants/pagination.js';
/**
 * Sprint 64.61.0 — this capped `page_size` at **100**, below the shared ceiling,
 * and no check could see it.
 *
 * `page-size-ceiling.test.ts` scan 1 fails any `z.…max(n)` on a `page_size` line
 * where `n < MAX_LIST_PAGE_SIZE`. This line is exactly that shape and passed,
 * because the scan walks `src/backend/src` and this file is in `src/shared`. A
 * working check whose scope is narrower than its subject — the ceiling has two
 * definitions again, which is the defect 62.8.0 removed.
 *
 * The cap and the default now read the constants, so there is no second number
 * to drift. Measured at the time of the fix: this schema has **zero consumers**
 * (`grep -rn PaginationSchema src/backend/src src/frontend/src` → no non-test
 * hits), so nothing was refusing a compliant request because of it — it was a
 * loaded gun rather than a fired one, and it is stated that way rather than
 * written up as a live defect.
 */
export const PaginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    page_size: z.coerce.number().int().min(1).max(MAX_LIST_PAGE_SIZE).default(DEFAULT_LIST_PAGE_SIZE),
});
export const IdParamSchema = z.object({
    id: z.string().min(1, 'ID is required'),
});
export const EmailSchema = z.string().email('Valid email is required').max(254);
export const PhoneSchema = z.string().max(20).nullable().optional();
export const DateSchema = z
    .string()
    .datetime()
    .or(z.string().regex(/^\d{4}-\d{2}-\d{2}$/));
/** The day `day` exists in `month` of `year` (Gregorian; `month` is 1-based). */
function dayExistsInMonth(year, month, day) {
    if (month < 1 || month > 12 || day < 1)
        return false;
    return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/**
 * A calendar date a person actually wrote: `YYYY-MM-DD`, with the day
 * existing in the month named. `DateSchema` above accepts the SHAPE
 * `2030-02-31`, and `new Date('2030-02-31')` silently rolls it to 3 March —
 * so shape plus parseability is not enough for a date a clock hangs from.
 *
 * Consolidated here from the sector-licensing module (Sprint 62.19.0, gate
 * E12.5 pass 1, P2-13); the same rule `isInstant` applies to date-times.
 */
export function isCalendarDate(value) {
    const shape = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!shape)
        return false;
    const [, y, m, d] = shape;
    return dayExistsInMonth(Number(y), Number(m), Number(d));
}
export const CalendarDateSchema = z.string().refine(isCalendarDate, {
    message: 'must be a calendar date (YYYY-MM-DD)',
});
/**
 * A calendar date (`YYYY-MM-DD`) moved by `days` calendar days; `days` may be
 * negative. Counted in UTC on the date itself, so no timezone or daylight-saving
 * change can move the result by a day: `2026-02-28` + 1 is `2026-03-01`,
 * `2028-02-28` + 1 is `2028-02-29`.
 *
 * Fix-sprint-20261031-1200 F30 — the one copy. The AI register's supplier
 * service, the controlled-testing case clock and the sample-package resolver
 * each carried their own. A caller validates its input with
 * {@link isCalendarDate} first: a date that does not exist (`2026-02-30`) is
 * counted from the day it rolls to, as `Date.UTC` reads it.
 */
export function addCalendarDays(date, days) {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/**
 * True only for a timestamp that names an INSTANT — an ISO 8601 date-time
 * carrying `Z` or an explicit ±HH:MM offset.
 *
 * Sprint 62.14.0 gate E12.5 pass 1, P0-1. An HTML `datetime-local` control
 * yields `"2026-08-25T14:30"` with no offset, and ECMAScript reads an
 * offset-less date-time as the LOCAL time of whatever process parses it. An
 * operator in Hanoi recording receipt at 14:30 ICT would have anchored the
 * clock at 14:30 UTC on a UTC container — seven hours late, which is more
 * than the whole of the 3-hour emergency data-provision clock that module
 * exists to run. The seam is closed at both ends: the browser converts local
 * wall-clock to an instant before posting, and the route refuses anything
 * that is not one, because a receipt time whose meaning depends on where the
 * server runs is not evidence of anything.
 *
 * Moved here from `authority-operations/direction-service.ts` in Sprint
 * 62.19.0 (gate E12.5 pass 1, P2-13) once a third module needed it.
 */
export function isInstant(value) {
    const shape = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/.exec(value);
    if (!shape)
        return false;
    if (Number.isNaN(new Date(value).getTime()))
        return false;
    // `new Date` silently ROLLS OVER an impossible calendar date — 31 February
    // becomes 3 March — so shape plus parseability is not enough. The day must
    // exist in the month named, or the receipt the whole clock hangs from is a
    // date the operator did not write.
    const [, y, m, d] = shape;
    return dayExistsInMonth(Number(y), Number(m), Number(d));
}
export const InstantSchema = z.string().refine(isInstant, {
    message: 'must be an ISO 8601 date-time carrying Z or a ±HH:MM offset',
});
export const UlidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Invalid ULID format');
export const UuidSchema = z.guid('Invalid UUID format');
export const NameSchema = z.string().min(1, 'Name is required').max(200);
export const DescriptionSchema = z.string().max(10000).nullable().optional();
/**
 * The pack-code grammar: one alphanumeric, then up to 199 of `[A-Za-z0-9._/-]`.
 *
 * Deliberately a SHAPE, not a list (R-46). The pack declares the codes and the
 * platform never compares one against a literal. Every code in
 * `packs/regulatory/*` is `[A-Z0-9_]+`; the pattern admits that plus `.`, `/`
 * and `-` for hyphenated placeholders and any future pack, and caps the length.
 */
export const FRAMEWORK_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,199}$/;
/** Accept a single string matching {@link FRAMEWORK_CODE_PATTERN}; anything else is `null`. */
export function parseFrameworkCode(value) {
    if (typeof value !== 'string')
        return null;
    if (!FRAMEWORK_CODE_PATTERN.test(value))
        return null;
    return value;
}
/**
 * The zod form, for routes that already carry a schema and validate through
 * `validateQuery` / `validateParams` (REUSE.md §*Validate Middleware*). Same
 * predicate, same brand — a second grammar would be the defect this replaces.
 */
export const FrameworkCodeSchema = z
    .string()
    .refine((v) => parseFrameworkCode(v) !== null, {
    message: 'must be a single framework code',
});
//# sourceMappingURL=common.js.map